import { type ReactNode } from 'react';
import { Trophy, ChevronDown } from 'lucide-react';
import type { Cfg } from '../hooks/use-settings-state';
import { NumField, ToggleField } from './settings-field';

export type RaceCycle = 'daily' | 'weekly' | 'monthly';

type Set = (k: keyof Cfg, v: string | number | boolean) => void;
type Keys = {
  enabled: keyof Cfg;
  threshold: keyof Cfg;
  amount: keyof Cfg;
};

const LABEL: Record<RaceCycle, string> = {
  daily: 'Diario',
  weekly: 'Semanal',
  monthly: 'Mensual',
};

export const RACE_KEYS: Record<RaceCycle, Keys> = {
  daily: {
    enabled: 'daily_first_to_x_enabled',
    threshold: 'daily_first_to_x_threshold',
    amount: 'daily_first_to_x_amount_usd',
  },
  weekly: {
    enabled: 'weekly_first_to_x_enabled',
    threshold: 'weekly_first_to_x_threshold',
    amount: 'weekly_first_to_x_amount_usd',
  },
  monthly: {
    enabled: 'monthly_first_to_x_enabled',
    threshold: 'monthly_first_to_x_threshold',
    amount: 'monthly_first_to_x_amount_usd',
  },
};

type Props = { cycle: RaceCycle; cfg: Cfg; set: Set };

function Header({ cycle, cfg, set }: Props): ReactNode {
  const k = RACE_KEYS[cycle];
  return (
    <summary className="flex cursor-pointer list-none items-center justify-between gap-sm py-xs [&>svg]:open:rotate-180">
      <div className="flex items-center gap-sm">
        <ChevronDown
          size={12}
          className="text-text-tertiary transition-transform"
        />
        <Trophy size={14} className="text-text-tertiary" />
        <span className="text-xs font-medium text-text-secondary">
          Bonus {LABEL[cycle]}
        </span>
      </div>
      <ToggleField
        label=""
        value={!!cfg[k.enabled]}
        onChange={(v) => set(k.enabled, v)}
      />
    </summary>
  );
}

function Fields({ cycle, cfg, set }: Props): ReactNode {
  const k = RACE_KEYS[cycle];
  return (
    <div className="grid grid-cols-2 gap-md pl-lg pt-sm">
      <NumField
        label="Umbral de creadores"
        value={cfg[k.threshold] as number}
        onChange={(v) => set(k.threshold, v)}
        tip="Primero en llegar a X gana"
      />
      <NumField
        label="Premio"
        value={cfg[k.amount] as number}
        onChange={(v) => set(k.amount, v)}
        unit="$"
      />
    </div>
  );
}

export function RaceCycleBlock({ cycle, cfg, set }: Props): ReactNode {
  return (
    <details className="col-span-full rounded-md border border-white/[0.04] px-sm">
      <Header cycle={cycle} cfg={cfg} set={set} />
      <Fields cycle={cycle} cfg={cfg} set={set} />
    </details>
  );
}
